import React from "react"
import { Box, Checkbox, Chip, Typography, Button } from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import moment from "moment"
import _ from "lodash"
import { process } from "@progress/kendo-data-query"
import { useTranslation } from "react-i18next"
import ActionMenu from "./ActionMenu"
import IconMenu from "./IconMenu"
import ActionDropdown from "./ActionDropdown"
import SaasView from "./SaasView"

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    background: "#f4f7fa",
    borderRadius: 8,
    padding: theme.spacing(1),
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  card: {
    background: "#fff",
    border: "1px solid #e8e8e8",
    borderRadius: 10,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    padding: "14px 14px 10px",
    cursor: "pointer",
    transition: "box-shadow 0.15s ease, border-color 0.15s ease",
    "&:active": {
      boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
    },
  },
  cardSelected: {
    borderColor: "var(--primaryColor, #1565C0)",
    background: "var(--lightColor, #f5f8ff)",
  },
  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 10,
    paddingBottom: 10,
    borderBottom: "1px solid #f0f0f0",
  },
  headerLeft: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    minWidth: 0,
    flex: 1,
  },
  title: {
    fontWeight: 700,
    fontSize: 15,
    color: "#1a1a1a",
    lineHeight: 1.3,
    wordBreak: "break-word",
  },
  subtitle: {
    fontSize: 12,
    color: "#6b6b6b",
    marginTop: 2,
    wordBreak: "break-word",
  },
  fields: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 8,
    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  field: {
    minWidth: 0,
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: "#8a8a8a",
    textTransform: "uppercase",
    letterSpacing: 0.3,
    marginBottom: 2,
  },
  fieldValue: {
    fontSize: 13,
    color: "#222",
    wordBreak: "break-word",
    lineHeight: 1.35,
  },
  empty: {
    textAlign: "center",
    padding: "40px 16px",
    color: "#777",
    background: "#fff",
    borderRadius: 10,
    border: "1px solid #e8e8e8",
  },
  pager: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 12,
    padding: "10px 8px",
    background: "#fff",
    borderRadius: 8,
    border: "1px solid #e8e8e8",
  },
  pagerInfo: {
    fontSize: 12,
    color: "#555",
  },
  pagerActions: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  statusChip: {
    height: 22,
    fontSize: 11,
    fontWeight: 600,
  },
  saasViewWrap: {
    width: "100%",
    minWidth: 0,
    "& #id-is-saas-view": {
      padding: 0,
      margin: 0,
    },
  },
}))

function formatDateUTC(isoString) {
  if (!isoString) return "-"
  const date = new Date(isoString)
  if (Number.isNaN(date.getTime())) return String(isoString)
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ]
  const day = String(date.getUTCDate()).padStart(2, "0")
  const month = months[date.getUTCMonth()]
  const year = date.getUTCFullYear()
  let hours = date.getUTCHours()
  const minutes = String(date.getUTCMinutes()).padStart(2, "0")
  const seconds = String(date.getUTCSeconds()).padStart(2, "0")
  const ampm = hours >= 12 ? "PM" : "AM"
  hours = hours % 12 || 12
  return `${day}-${month}-${year} ${String(hours).padStart(
    2,
    "0"
  )}:${minutes}:${seconds} ${ampm}`
}

function resolveFieldValue(dataItem, column) {
  if (!column?.field) return "-"
  const raw = _.get(dataItem, column.field)
  if (raw === null || raw === undefined || raw === "") return "-"

  if (column.isDateForceFormat || column.isDirectDateFormat) {
    return formatDateUTC(raw)
  }
  if (column.isDateFormat || column.date) {
    const m = moment(raw)
    return m.isValid() ? m.format("DD-MMM-YYYY HH:mm:ss") : String(raw)
  }
  if (column.boolean || column.isBoolean) {
    return raw ? "Yes" : "No"
  }
  if (column.adjustmentType) {
    return String(raw) === "true" ? "Increment" : "Decrement"
  }
  if (column.renderFilterCell && Array.isArray(column.filterData)) {
    const found = column.filterData.find(
      (f) => f.value?.toString() === raw?.toString()
    )
    return found?.tab ?? String(raw)
  }
  if (column.colorFilterArr && Array.isArray(column.colorFilterArr)) {
    const found = column.colorFilterArr.find((f) => raw === f.value)
    return found?.label || found?.name || String(raw)
  }
  if (_.isArray(raw)) {
    return raw
      .map((item) =>
        _.isObject(item) ? item.name || item.label || JSON.stringify(item) : item
      )
      .join(", ")
  }
  if (_.isObject(raw)) {
    return raw.name || raw.label || raw.title || raw.status || "-"
  }
  if (typeof raw === "boolean") {
    return raw ? "Yes" : "No"
  }
  return String(raw)
}

function MobileTabletCardList({
  columnData = [],
  rowData,
  gridState,
  nonApiDatastate = false,
  number_of_items,
  setGridState,
  pageable = true,
  pagination = true,
  ShowCheckBox = true,
  isInlineEditAllowed = false,
  selectionChange = () => {},
  onRowClick = () => null,
  isOnRowClick = true,
  hideCheckboxAll = false,
  isDirectClick = false,
  onEdit = () => {},
  onDelete = () => {},
  onDiscard = () => {},
  onSave = () => {},
  onView = () => {},
  isLookup = false,
  isDisableForceNoActionMenu = false,
  actionMenu,
  actionDropdown = false,
  handleSaveAction = () => {},
  additionalActions = () => {},
  forceHideEdit = false,
  deleteIcon = false,
  deleteIconHolder = false,
  tooltipColors = {},
  deleteDisabled = () => false,
  deleteTooltip = () => "Delete",
  onDeleteDisabled = () => {},
  menutype = "",
  historyPopup = true,
  isDisableViewIcon = false,
  actionEdit = true,
  actionDelete = true,
  gridNoRecordsText = "No records found",
  renderCardActions = null,
  emptyContent = null,
  getSelectRowItem = "",
  onUpdateSelectRowItem = () => "",
}) {
  const classes = useStyles()
  const { t } = useTranslation()

  const visibleColumns = _.filter(columnData, { show: true })
  const processed = nonApiDatastate
    ? process(rowData?.data || [], gridState?.dataState || {})
    : { data: rowData?.data || [], total: number_of_items }

  const rows = processed?.data || []
  const total = nonApiDatastate
    ? processed?.total ?? rows.length
    : number_of_items ?? rows.length

  const skip = gridState?.dataState?.skip || 0
  const take = gridState?.dataState?.take || 50
  const page = Math.floor(skip / take) + 1
  const pageCount = Math.max(1, Math.ceil(total / take))
  const from = total === 0 ? 0 : skip + 1
  const to = Math.min(skip + take, total)

  const titleColumn = visibleColumns[0]
  const subtitleColumn = visibleColumns[1]
  const detailColumns = visibleColumns.slice(
    titleColumn && subtitleColumn ? 2 : titleColumn ? 1 : 0
  )

  const goToPage = (nextPage) => {
    const nextSkip = (nextPage - 1) * take
    setGridState((c) => ({
      dataState: { ...c.dataState, skip: nextSkip },
    }))
  }

  const handleCardClick = (dataItem, index) => {
    const event = { dataItem, dataIndex: index }
    if (hideCheckboxAll) selectionChange(event)
    if (isOnRowClick && _.isFunction(onRowClick)) onRowClick(event)
    if (isDirectClick) onEdit(event)
  }

  const renderSaasView = (dataItem, column) => {
    if (!column?.isSaasView) return null
    return (
      <Box
        id="id-is-saas-view"
        className={classes.saasViewWrap}
        onClick={(e) => e.stopPropagation()}
      >
        <SaasView
          onUpdateSelectRowItem={onUpdateSelectRowItem}
          getSelectRowItem={getSelectRowItem}
          dataItem={dataItem}
          value={dataItem?.[column.field]}
        />
      </Box>
    )
  }

  const renderActions = (dataItem, index) => {
    if (_.isFunction(renderCardActions)) {
      return renderCardActions({ dataItem, dataIndex: index })
    }
    if (isLookup || isDisableForceNoActionMenu) return null

    const props = { dataItem, dataIndex: index, field: "" }

    if (actionMenu) {
      if (actionDropdown) {
        return (
          <ActionDropdown
            props={props}
            dataItem={dataItem}
            value={dataItem}
            handleChange={(event) => handleSaveAction(event, props)}
          />
        )
      }
      return (
        <ActionMenu
          forceHideEdit={forceHideEdit}
          data={dataItem}
          additionalMenus={additionalActions}
          isInlineEditAllowed={
            typeof dataItem.isRowEditable !== "undefined"
              ? dataItem.isRowEditable
              : isInlineEditAllowed
          }
          deleteIcon={deleteIcon}
          deleteIconHolder={deleteIconHolder}
          tooltipColors={tooltipColors}
          deleteDisabled={deleteDisabled}
          deleteTooltip={deleteTooltip}
          onDeleteDisabled={onDeleteDisabled}
          in_edit={() => onEdit(props)}
          delete={() => onDelete(props)}
          cancel={() => onDiscard(props)}
          save={() => onSave(props)}
          menutype={menutype}
          historyPopup={historyPopup}
        />
      )
    }

    return (
      <IconMenu
        isDisableViewIcon={isDisableViewIcon}
        actionEdit={actionEdit}
        actionDelete={actionDelete}
        data={dataItem}
        isInlineEditAllowed={
          typeof dataItem.isRowEditable !== "undefined"
            ? dataItem.isRowEditable
            : isInlineEditAllowed
        }
        in_edit={() => onEdit(props)}
        delete={() => onDelete(props)}
        cancel={() => onDiscard(props)}
        save={() => onSave(props)}
        view={() => onView(props)}
      />
    )
  }

  if (!rows.length) {
    return (
      <Box className={classes.root}>
        <Box className={classes.empty}>
          {emptyContent != null ? emptyContent : gridNoRecordsText}
        </Box>
      </Box>
    )
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.list}>
        {rows.map((dataItem, index) => {
          const titleValue = titleColumn
            ? resolveFieldValue(dataItem, titleColumn)
            : `Row ${skip + index + 1}`
          const subtitleValue = subtitleColumn
            ? resolveFieldValue(dataItem, subtitleColumn)
            : ""

          return (
            <Box
              key={dataItem.id || dataItem._id || `${skip}-${index}`}
              className={`${classes.card} ${
                dataItem.selected ? classes.cardSelected : ""
              }`}
              onClick={() => handleCardClick(dataItem, index)}
            >
              <Box className={classes.cardHeader}>
                <Box className={classes.headerLeft}>
                  {ShowCheckBox && isInlineEditAllowed === true && (
                    <Checkbox
                      color="primary"
                      size="small"
                      checked={!!dataItem.selected}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        selectionChange({
                          dataItem: {
                            ...dataItem,
                            selected: e.target.checked,
                          },
                          dataIndex: index,
                        })
                      }}
                    />
                  )}
                  <Box minWidth={0} className={classes.saasViewWrap}>
                    {titleColumn?.isSaasView ? (
                      renderSaasView(dataItem, titleColumn)
                    ) : (
                      <>
                        <Typography className={classes.title}>
                          {titleValue}
                        </Typography>
                        {subtitleValue && subtitleValue !== "-" && (
                          <Typography className={classes.subtitle}>
                            {subtitleColumn?.title
                              ? `${t(subtitleColumn.title)}: ${subtitleValue}`
                              : subtitleValue}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                </Box>
                <Box onClick={(e) => e.stopPropagation()}>
                  {renderActions(dataItem, index)}
                </Box>
              </Box>

              {detailColumns.length > 0 && (
                <Box className={classes.fields}>
                  {detailColumns.map((column) => {
                    if (column.actionButton || column.actionComments) {
                      return null
                    }
                    if (column.isSaasView) {
                      return (
                        <Box
                          key={column.field || column.title}
                          className={classes.field}
                        >
                          <Typography className={classes.fieldLabel}>
                            {t(column.title || column.field)}
                          </Typography>
                          {renderSaasView(dataItem, column)}
                        </Box>
                      )
                    }
                    const value = resolveFieldValue(dataItem, column)
                    const isStatusLike =
                      /status/i.test(column.field || "") ||
                      /status/i.test(column.title || "")

                    return (
                      <Box key={column.field || column.title} className={classes.field}>
                        <Typography className={classes.fieldLabel}>
                          {t(column.title || column.field)}
                        </Typography>
                        {isStatusLike && value !== "-" ? (
                          <Chip
                            size="small"
                            label={value}
                            className={classes.statusChip}
                            style={{
                              background: "#eef3f8",
                              color: "#1a1a1a",
                            }}
                          />
                        ) : (
                          <Typography className={classes.fieldValue}>
                            {value}
                          </Typography>
                        )}
                      </Box>
                    )
                  })}
                </Box>
              )}
            </Box>
          )
        })}
      </Box>

      {pageable && pagination && total > 0 && (
        <Box className={classes.pager}>
          <Typography className={classes.pagerInfo}>
            {from}-{to} of {total} items
          </Typography>
          <Box className={classes.pagerActions}>
            <Button
              size="small"
              variant="outlined"
              disabled={page <= 1}
              onClick={() => goToPage(page - 1)}
            >
              Prev
            </Button>
            <Typography className={classes.pagerInfo}>
              {page} / {pageCount}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              disabled={page >= pageCount}
              onClick={() => goToPage(page + 1)}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default MobileTabletCardList
export { MobileTabletCardList }
